import React, { useState } from 'react';
import { Gamepad2, Lock, Shield, Sparkles } from 'lucide-react';
import { acceptDashboardInvite, isSupabaseConfigured, signInWithPassword, signUpWithInvite } from './supabaseClient';
import { buildDashboardInviteUrl, isUsingLocalDashboardBase } from './urlHelpers';

function AuthScreen({ inviteToken, onAuthenticated }) {
  const [mode, setMode] = useState(inviteToken ? 'signup' : 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [status, setStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus('');
    setIsSubmitting(true);

    try {
      if (!isSupabaseConfigured) {
        onAuthenticated({
          session: null,
          profile: { id: 'local-admin', email: 'local-admin', name: 'Local Admin', role: 'admin' }
        });
        return;
      }

      if (mode === 'signup') {
        const session = await signUpWithInvite({
          email,
          password,
          name,
          inviteToken,
          redirectTo: buildDashboardInviteUrl(inviteToken)
        });
        if (session?.access_token) {
          await acceptDashboardInvite({ accessToken: session.access_token, inviteToken, name });
          onAuthenticated({ session, shouldLoadProfile: true });
          return;
        }

        setStatus(isUsingLocalDashboardBase()
          ? 'Account created. The confirmation email may still point to localhost until REACT_APP_DASHBOARD_URL is set to the production dashboard URL.'
          : 'Account created. Check the confirmation email; it should bring you back here to finish login.');
        setMode('login');
        return;
      }

      const session = await signInWithPassword(email, password);
      if (inviteToken) {
        await acceptDashboardInvite({ accessToken: session.access_token, inviteToken, name });
      }
      onAuthenticated({ session, shouldLoadProfile: true });
    } catch (error) {
      setStatus(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="auth-screen min-h-screen bg-gray-950 text-white flex items-center justify-center p-6">
      <div className="auth-card mobile-card w-full max-w-md bg-gray-900 border border-gray-800 rounded-xl p-6">
        <div className="creator-eyebrow mb-4"><Gamepad2 size={14} /> VASIA // CREATOR OPS</div>
        <div className="flex items-center gap-3 mb-5">
          <div className="auth-lock w-10 h-10 rounded-lg bg-purple-600/20 border border-purple-500/40 flex items-center justify-center">
            <Lock size={20} className="text-purple-300" />
          </div>
          <div>
            <h1 className="text-xl font-bold">Ready, player one?</h1>
            <p className="text-sm text-gray-400">{mode === 'signup' ? 'Create agency analytics access' : 'Sign in to continue'}</p>
          </div>
        </div>

        <div className="auth-flavour"><Sparkles size={14} /> Revenue, audience and content — all in one place.</div>

        {!isSupabaseConfigured && (
          <div className="mb-4 rounded-lg border border-yellow-500/40 bg-yellow-950/30 p-3 text-sm text-yellow-200 flex gap-2">
            <Shield size={16} className="mt-0.5" />
            <span>Supabase env vars are not configured. Continuing opens a local admin preview only.</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'signup' && (
            <div>
              <label className="block text-xs text-gray-400 mb-1">Name</label>
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-purple-500"
                placeholder="Your name"
              />
            </div>
          )}

          <div>
            <label className="block text-xs text-gray-400 mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-purple-500"
              placeholder="you@example.com"
              required={isSupabaseConfigured}
            />
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Password</label>
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-purple-500"
              placeholder="Password"
              required={isSupabaseConfigured}
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 rounded-lg px-4 py-2 text-sm font-medium transition-colors"
          >
            {isSubmitting ? 'Working...' : mode === 'signup' ? 'Create account' : 'Sign in'}
          </button>
        </form>

        <button
          onClick={() => setMode(mode === 'signup' ? 'login' : 'signup')}
          className="w-full mt-4 text-sm text-gray-400 hover:text-white"
        >
          {mode === 'signup' ? 'Already have an account? Sign in' : 'Have an invite? Create an account'}
        </button>

        {status && (
          <div className="mt-4 rounded-lg bg-gray-800/70 border border-gray-700 p-3 text-sm text-gray-300">
            {status}
          </div>
        )}
      </div>
    </div>
  );
}

export default AuthScreen;
